import React, { useEffect, useMemo, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { stripEchartsAnimation } from '../motion';

type Props = React.ComponentProps<typeof ReactECharts>;

/**
 * An ECharts chart that self-resizes to its container via a `ResizeObserver`.
 *
 * The transcript lives inside a tab that stays mounted but goes `display:none` when inactive
 * (so the conversation + charts survive tab switches). A hidden container measures 0×0, so a chart
 * first laid out while hidden renders collapsed — and ECharts then *pins* that fixed 0 width: a bare
 * `resize()` re-applies the captured size, so it never recovers. Passing `{width,height:'auto'}`
 * forces ECharts to re-measure the now-laid-out container.
 *
 * CRITICAL: `inst.resize()` re-renders every series with `animation:{duration:0}` (see ECharts
 * `ECharts.prototype.resize` → `updateMethods.update`), which SNAPS the chart to its final state and
 * KILLS any in-progress entrance draw. So we must only resize when the instance's current canvas size
 * actually disagrees with the laid-out container (the hidden→visible recovery) — never on a normal
 * mount where the size already matches, which would clobber the part-by-part build.
 */
export function ResponsiveECharts(props: Props) {
  const ref = useRef<any>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Single threading point for every catalog chart's reduced-motion gate: strip ECharts animation
  // (including nested per-series keys) so the chart paints at its final state with no draw-in pass.
  const reduceMotion = useReducedMotion();
  const option = useMemo(
    () => (reduceMotion && props.option ? stripEchartsAnimation(props.option) : props.option),
    [reduceMotion, props.option],
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      // getEchartsInstance is only present once the chart has mounted; guard for safety.
      const inst = ref.current?.getEchartsInstance?.();
      if (!inst) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return; // still hidden/collapsed — nothing to fit to yet
      // Only resize on a REAL mismatch (chart was laid out while the tab was hidden at 0×0 and is now
      // visible). On a normal mount the instance already measured the right size, so resizing would
      // re-render at duration:0 and cancel the entrance animation — skip it.
      if (Math.abs(inst.getWidth() - w) > 1 || Math.abs(inst.getHeight() - h) > 1) {
        inst.resize({ width: 'auto', height: 'auto' });
      }
    };
    const raf = requestAnimationFrame(fit);
    if (typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    // role=img + label so the otherwise-silent <canvas> has an accessible name. The plain-language query
    // (QueryReveal, rendered just above the chart) carries the detailed description for assistive tech.
    <div ref={wrapRef} role="img" aria-label="Data chart" style={{ width: '100%' }}>
      {/* autoResize=false disables echarts-for-react's built-in size-sensor, which calls resize()
          (a duration:0 re-render that would CANCEL the entrance draw) on any post-mount size change —
          e.g. the readout demote shifting layout the instant the chart mounts. Our own guarded
          ResizeObserver above handles the only resize we actually need (hidden-tab 0×0 recovery). */}
      <ReactECharts ref={ref} {...props} option={option} autoResize={false} />
    </div>
  );
}
